import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Beef, Egg, Fish, Leaf, MessageCircle, Search } from 'lucide-react';
import { produceAPI } from '@/lib/api';
import HomeShell from '@/components/home/HomeShell';
import { HomeSectionTitle } from '@/components/home/HomeBlocks';
import OfferCard from '@/components/produce/OfferCard';

const CATEGORY_TILES = [
  { key: 'crops', icon: Leaf, color: 'bg-[#388E3C]' },
  { key: 'animals', icon: Beef, color: 'bg-[#F4511E]' },
  { key: 'poultry', icon: Egg, color: 'bg-[#E59400]' },
  { key: 'fish', icon: Fish, color: 'bg-[#0288D1]' },
];

/** Offtaker home: search farm produce, browse by type, messages, and what was just added. */
export default function OfftakerHome() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [latest, setLatest] = useState([]);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    produceAPI.market({ limit: 6 }).then((res) => setLatest(res.data.offers)).catch(() => {});
    produceAPI.unread().then((res) => setUnread(res.data.unread)).catch(() => {});
  }, []);

  const search = (e) => {
    e.preventDefault();
    navigate(q.trim() ? `/produce?q=${encodeURIComponent(q.trim())}` : '/produce');
  };

  return (
    <HomeShell>
      <h2 className="font-heading text-xl font-bold text-farm-forest mb-2" data-testid="offtaker-search-label">
        {t('produceMarket.home.searchLabel')}
      </h2>
      <form onSubmit={search} className="relative mb-4">
        <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('produceMarket.market.search')}
               className="w-full h-14 rounded-full border border-gray-200 bg-white pl-12 pr-4 text-base shadow-sm"
               data-testid="offtaker-search" />
      </form>
      {/* Solid coloured buttons, same style as the other homes' tiles. */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        {CATEGORY_TILES.map(({ key, icon: Icon, color }) => (
          <button key={key} type="button" onClick={() => navigate(`/produce?category=${key}`)}
                  className={`${color} rounded-3xl shadow-md min-h-[120px] px-3 py-4 flex flex-col items-center justify-center gap-2 active:scale-[0.97] transition-transform`}
                  data-testid={`offtaker-cat-${key}`}>
            <Icon className="w-12 h-12 text-white drop-shadow-sm" strokeWidth={2} />
            <span className="font-heading font-extrabold text-lg text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.25)]">
              {t(`produce.categories.${key}`)}
            </span>
          </button>
        ))}
      </div>
      <button type="button" onClick={() => navigate('/produce/chats')}
              className="w-full flex items-center gap-3 rounded-2xl bg-white shadow-sm px-4 py-3 mb-4"
              data-testid="offtaker-messages">
        <MessageCircle className="w-6 h-6 text-farm-primary" />
        <span className="flex-1 text-left font-semibold">{t('produceMarket.chats.title')}</span>
        {unread > 0 && <span className="rounded-full bg-red-600 text-white text-xs px-2 py-0.5">{unread}</span>}
      </button>
      <HomeSectionTitle trailing={(
        <button type="button" className="text-sm font-semibold text-farm-primary" onClick={() => navigate('/produce')}>
          {t('produceMarket.home.seeAll')}
        </button>
      )}>{t('produceMarket.home.latest')}</HomeSectionTitle>
      <div className="space-y-3 pb-4">
        {latest.length === 0 && <p className="text-gray-600 text-sm">{t('produceMarket.market.none')}</p>}
        {latest.map((offer) => (
          <OfferCard key={`${offer.kind}-${offer.key}`} offer={offer}
                     onMessage={() => navigate(`/produce?q=${encodeURIComponent(offer.item)}`)} />
        ))}
      </div>
    </HomeShell>
  );
}
